import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";

const formatDriveImage = (url) => {
  if (!url) return null;
  if (url.includes("drive.google.com")) {
    const match = url.match(/\/d\/([^/]+)/);
    if (match?.[1]) {
      return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w1200`;
    }
  }
  return url;
};

const CaseStudyDetail = () => {
  window.scrollTo(0, 0);
  const { slug } = useParams();
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetch("https://sattva-backend.demetrix.in/case_study")
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          const found = data.data.find(
            (cs) =>
              cs.slug === slug &&
              cs.is_published &&
              !cs.is_deleted &&
              !cs.deleted_at
          );
          setCaseStudy(found || null);
        }
      })
      .catch((error) => console.error("Error fetching case study:", error))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950">
        <div className="flex flex-col items-center gap-4">
          {/* Spinner */}
          <div className="w-12 h-12 border-4 border-gray-300 dark:border-slate-700 
                        border-t-gray-900 dark:border-t-white 
                        rounded-full animate-spin">
          </div>

          {/* Optional text */}
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Loading case study...
          </p>
        </div>
      </div>
    );
  }


  if (!caseStudy) {
    return (
      <div className="pt-32 text-center text-xl font-medium text-gray-900 dark:text-gray-100">
        Case study not found
      </div>
    );
  }

  const imageMedia = caseStudy.media?.find((m) => m.type === "image");
  const imageUrl = formatDriveImage(imageMedia?.url);

  const videoMedia = caseStudy.media?.find((m) => m.type === "video");
  let videoUrl = null;
  if (videoMedia?.url) {
    const match = videoMedia.url.match(/(?:v=|youtu\.be\/)([^&?]+)/);
    if (match?.[1]) {
      videoUrl = `https://www.youtube.com/embed/${match[1]}`;
    }
  }

  // For typing animation duration
  // const typingDuration = Math.min(7, Math.max(3, caseStudy.title.length * 0.12));
  // const typingSteps = caseStudy.title.length;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-900 dark:text-gray-100 pt-32 pb-20 px-4 md:px-6 transition-colors duration-300">
      <article className="max-w-5xl mx-auto">
        {/* ==================== HERO ==================== */}
        <section className="my-8">
          {/* Text content – above the image */}
          <div className="text-left mb-8">
            <div className="flex flex-col md:flex-row items-center justify-between md:gap-4 mb-4">
              {/* <h1
                className={`
                text-3xl md:text-4xl font-bold 
                text-gray-900 dark:text-white 
                tracking-tight mb-4
                overflow-hidden whitespace-nowrap border-r-4 border-emerald-500
                animate-typing
                
              `}
                style={{
                  animation: `typing ${typingDuration}s steps(${typingSteps}) infinite, blink 0.8s step-end infinite`,
                }}
              >
                {caseStudy.title}
              </h1> */}
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">{caseStudy?.title}</h1>

              <div className="">
                <button
                  onClick={() => navigate(-1)}
                  className="px-4 py-2 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary/10 transition-colors dark:text-primary dark:hover:bg-primary/20"
                >
                  Close
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-6  text-emerald-800 dark:text-emerald-300 mb-4 animate-fadeIn">
              Client: {caseStudy.client}
            </p>

            {/* <p className="text-lg text-gray-600 dark:text-gray-400 font-light animate-fadeIn delay-200">
              Delivering on sustainability promises with world-class environmental management
            </p> */}
          </div>

          {/* Image – below the text */}
          {imageUrl && (
            <img
              src={imageUrl}
              alt={caseStudy.title}
              className="w-full max-h-[500px] object-cover rounded-xl shadow-2xl 
                         dark:shadow-black/60 shadow-black/10 transition-shadow duration-300"
              loading="lazy"
              referrerPolicy="no-referrer"
              onError={(e) => {
                e.currentTarget.src = "/placeholder.jpg"; // optional fallback
              }}
            />
          )}
        </section>

        {/* ==================== VIDEO ==================== */}
        {videoUrl && (
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-6 text-center">
              Case Study Video
            </h2>
            <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-2xl shadow-2xl bg-gray-100 dark:bg-slate-800">
              <iframe
                src={videoUrl}
                title={`${caseStudy.title} - Video`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full border-0"
              />
            </div>
          </section>
        )}

        {/* ==================== CARDS ==================== */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {["Challenge", "Solution", "Result"].map((title) => {
            const key = title.toLowerCase();

            return (
              <div
                key={title}
                className="
          p-6 rounded-xl
          bg-white dark:bg-slate-900
          border border-gray-200 dark:border-slate-700
          text-gray-800 dark:text-gray-300
        "
              >
                <h3 className="text-lg font-medium mb-3 text-gray-900 dark:text-gray-100">
                  {title}
                </h3>

                <p className="text-base leading-relaxed text-gray-700 dark:text-gray-400">
                  {caseStudy[key] || `No ${title.toLowerCase()} description available.`}
                </p>
              </div>
            );
          })}
        </section>


        {/* ==================== BACK BUTTON ==================== */}
        {/* <div className="text-center">
          <button
            onClick={() => window.history.back()}
            className={`
              inline-flex items-center gap-2 px-8 py-4
              bg-emerald-700 hover:bg-emerald-800 
              dark:bg-emerald-600 dark:hover:bg-emerald-700
              text-white font-semibold text-lg
              rounded-xl shadow-xl hover:shadow-2xl
              transition-all duration-200
            `}
          >
            ← Back to Case Studies
          </button>
        </div> */}
      </article>

      {/* Keyframes for animations */}
      <style>{`
        @keyframes typing {
          from { width: 0 }
          to { width: 40% }
        }
        @keyframes blink {
          50% { border-color: transparent }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-typing {
          width: 0;
          animation: typing var(--typing-duration, 4s) steps(var(--typing-steps, 30)) forwards,
                     blink 0.8s step-end infinite;
        }
        .animate-fadeIn {
          animation: fadeIn 1s ease-out forwards;
        }
        .delay-200 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
};

export default CaseStudyDetail;